import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useMatch } from '@tanstack/react-location';
import { Mapping } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/mapping.pb';
import { Provider } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/provider.pb';
import dayjs from 'dayjs';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useAiScheduleAccess } from '@frontend/ai-receptionist-settings';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useGetAppointmentColorsEnabled } from '@frontend/schedule-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { ConfirmationModal, SpinningLoader, Text, useAlert, useModalControl } from '@frontend/design-system';
import { DATETIME_FORMATS } from '../../constants';
import { useScheduleSettingsSmallMode } from '../../hooks/settings/use-schedule-settings-small-mode';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { PageConfig } from '../../types';
import { ManagePractitionersV3CompactTable } from './ManagePractitionersV3CompactTable';
import { ManagePractitionersV3LargeTable } from './ManagePractitionersV3LargeTable';

type ManagePractitionersV3TableProps = {
  locationId?: string;
  pageConfig: PageConfig;
  mappingList: Mapping[];
  isLoading: boolean;
  setPageConfig: Dispatch<SetStateAction<PageConfig>>;
  debouncedSearchValue: string | undefined;
  disableProviderActions?: boolean;
};

export const ManagePractitionersV3Table = ({
  locationId,
  isLoading,
  pageConfig,
  mappingList,
  setPageConfig,
  debouncedSearchValue,
  disableProviderActions = false,
}: ManagePractitionersV3TableProps) => {
  const alert = useAlert();
  const {
    params: { locationId: urlParamLocationId },
  } = useMatch<{ Params: { locationId?: string } }>();
  const { t } = useTranslation('scheduleAppointmentTypes');
  const { isSmallMode } = useScheduleSettingsSmallMode();
  const { modalProps, triggerProps } = useModalControl();
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const selectedProviderId = selectedProvider?.id || '';
  const selectedProviderDisplayName = selectedProvider?.displayName || '';
  const { canUseAppointmentColors } = useGetAppointmentColorsEnabled();
  const { isIntegratedOffice } = DataSourcesQueries.useGetAccessibleIntegrations();
  const { isScheduleDefinitionsEnabled } = SchedulerV3.Helpers.useGetScheduleDefinitionsFeatureFlagDetails();
  const { getOnlineSchedulingReadyLocationIds } = useSchedulingLocationInfo();

  const { hasAiScheduleAccess } = useAiScheduleAccess();
  const isOnlineSchedulingEnabled = getOnlineSchedulingReadyLocationIds().length > 0;
  const showOnlineSchedulingOptions = isOnlineSchedulingEnabled && !hasAiScheduleAccess;
  const { invalidateListProviders, invalidateListMappings } = SchedulerV3.Helpers.useSchedulerV3InvalidateQueries();
  const { locationTimezoneMap } = SchedulerV3.Helpers.useScheduleLocationTimezoneShallowStore('locationTimezoneMap');
  const timezoneDetails = locationTimezoneMap?.[urlParamLocationId || locationId || ''] || {};
  const locationTodayDate = dayjs().tz(timezoneDetails?.timezone).startOf('day');

  const { hasWSM } = SchedulerV3.Helpers.useCheckHasWSM(locationId);

  const { isSingleTypeScope, selectedGroupId } = useAppScopeStore();
  const isMultiLocation = !isSingleTypeScope;

  const providerEventsQuery = SchedulerV3.Queries.useListCalendarEventsByOwnerQuery({
    request: {
      ownerId: selectedProviderId,
      limit: 1,
      page: 1,
      startDateTime: locationTodayDate.format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET),
      endDateTime: locationTodayDate.add(1, 'year').format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET),
      locationId: urlParamLocationId || locationId,
    },
    options: { enabled: !!selectedProviderId, retry: 1, staleTime: 1, cacheTime: 1 },
  });

  const upcomingEventCount = providerEventsQuery.data ? Number(providerEventsQuery.data?.totalCount) : 0;

  const { navigate } = useSettingsNavigate();

  const navigateTo = useCallback(
    ({ providerId }: { providerId: string }) => {
      if (urlParamLocationId) {
        navigate({
          to: '/schedule/manage-providers/:locationId/edit/:providerId',
          params: { providerId: providerId ?? '', locationId: urlParamLocationId ?? '' },
        });
      } else {
        navigate({
          to: '/schedule/manage-providers/edit/:providerId',
          params: { providerId: providerId ?? '' },
        });
      }
    },
    [navigate, urlParamLocationId]
  );

  const { mutateAsync: updateProvider } = SchedulerV3.Mutations.useUpdateProviderMutation({ optimisticUpdate: true });

  const archiveProviderMutation = SchedulerV3.Mutations.useArchiveProviderMutation();

  const handlePractitionerArchive = useCallback(
    async (practitioner: Provider) => {
      const isArchived = practitioner.isArchived;

      try {
        if (isArchived) {
          await updateProvider({
            id: practitioner.id,
            firstName: practitioner.firstName || '',
            lastName: practitioner.lastName || '',
            isArchived: false,
            hidden: practitioner.hidden,
            childLocationId: isMultiLocation ? practitioner.locationId : undefined,
          });
        } else {
          await archiveProviderMutation.mutateAsync({
            id: practitioner.id,
            locationId: isMultiLocation ? selectedGroupId : practitioner.locationId,
          }); // @TODO: Handle optimistic update
          await updateProvider({
            id: practitioner.id,
            firstName: practitioner.firstName || '',
            lastName: practitioner.lastName || '',
            hidden: practitioner.hidden,
            profile: { ...practitioner.profile, sobs: false },
            childLocationId: isMultiLocation ? practitioner.locationId : undefined,
          });
          invalidateListProviders(locationId);
          invalidateListMappings(locationId);
        }
        alert.success(isArchived ? t('Successfully unarchived provider') : t('Successfully archived provider'));
      } catch (error) {
        alert.error(isArchived ? t('Failed to unarchive provider') : t('Failed to archive provider'));
      }
    },
    [
      alert,
      archiveProviderMutation,
      t,
      updateProvider,
      isMultiLocation,
      selectedGroupId,
      invalidateListProviders,
      invalidateListMappings,
      locationId,
    ]
  );

  const tableRowsAction = useMemo(() => {
    if (disableProviderActions) {
      return { actions: [], isOnlineSchedulingEnabled: false };
    }
    return {
      actions: [
        {
          Icon: () => <Icon name='edit-small' />,
          label: t('Edit'),
          hide: (row: Provider) => row.isArchived,
          onClick: ({ id }: Provider) => navigateTo({ providerId: id }),
        },
        {
          Icon: () => <Icon name='archive-small' color='error' />,
          label: t('Archive'),
          css: { span: { color: theme.colors.critical50 } },
          hide: (row: Provider) => !hasWSM || row.isArchived,
          onClick: (practitioner: Provider) => {
            triggerProps.onClick();
            setSelectedProvider(practitioner);
          },
        },
        {
          Icon: () => <Icon name='unarchive-small' color='primary' />,
          label: t('Unarchive'),
          css: { span: { color: theme.colors.primary50 } },
          hide: (row: Provider) => !hasWSM || !row.isArchived,
          onClick: (practitioner: Provider) => handlePractitionerArchive(practitioner),
        },
      ],
    };
  }, [disableProviderActions, t, navigateTo, hasWSM, triggerProps, handlePractitionerArchive]);

  const tableProps = {
    locationId,
    isLoading,
    pageConfig,
    mappingList,
    setPageConfig,
    tableRowsAction,
    canUseAppointmentColors,
    isIntegratedOffice,
    isScheduleDefinitionsEnabled,
    isOnlineSchedulingEnabled: showOnlineSchedulingOptions,
    debouncedSearchValue,
    hasAiScheduleAccess,
  };

  return (
    <>
      {isSmallMode ? (
        <ManagePractitionersV3CompactTable {...tableProps} />
      ) : (
        <ManagePractitionersV3LargeTable {...tableProps} />
      )}
      <ConfirmationModal
        {...modalProps}
        destructive
        maxWidth={!!upcomingEventCount ? 600 : 400}
        title={t('Are you sure you want to deactivate {{provider}}?', {
          provider: selectedProviderDisplayName,
        })}
        message={
          <ConfirmationModalMessage
            isLoading={providerEventsQuery.isLoading}
            provider={selectedProviderDisplayName}
            upcomingEventCount={upcomingEventCount}
          />
        }
        confirmLabel={t('Deactivate')}
        cancelLabel={t('Cancel')}
        onCancel={modalProps.onClose}
        onConfirm={() => {
          if (selectedProvider) {
            handlePractitionerArchive(selectedProvider);
            setSelectedProvider(null);
          }
        }}
      />
    </>
  );
};

type ConfirmationModalMessageProps = {
  isLoading: boolean;
  provider: string;
  upcomingEventCount: number;
};

const ConfirmationModalMessage = ({ isLoading, provider, upcomingEventCount }: ConfirmationModalMessageProps) => {
  const { t } = useTranslation('scheduleAppointmentTypes');

  if (isLoading) {
    return (
      <div css={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(2) }}>
        <SpinningLoader />
      </div>
    );
  }

  // No upcoming appointment
  if (upcomingEventCount === 0) {
    return (
      <Text>
        {t('This action will remove {{provider}} from the schedule. Existing appointments will not be affected.', {
          provider,
        })}
      </Text>
    );
  }

  // Single upcoming appointment
  if (upcomingEventCount === 1) {
    return (
      <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
        <Text as='span'>
          {t('{{provider}} currently has {{count}} upcoming appointments scheduled. If you deactivate this provider:', {
            provider,
            count: upcomingEventCount,
          })}
        </Text>
        <ul>
          <Text as='li'>
            {t('Upcoming appointment will be marked as “Unassigned”', {
              count: upcomingEventCount,
            })}
          </Text>
          <Text as='li'>{t("You'll need to reassign the appointment manually")}</Text>
        </ul>
        <Text as='span'>
          {t('We recommend reassigning the appointment before deactivating {{provider}}', {
            provider,
          })}
        </Text>
      </div>
    );
  }

  // Multiple upcoming appointments
  return (
    <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
      <Text as='span'>
        {t('{{provider}} currently has {{count}} upcoming appointments scheduled. If you deactivate this provider:', {
          count: upcomingEventCount,
          provider,
        })}
      </Text>
      <ul>
        <Text as='li'>
          {t('All {{count}} upcoming appointments will be marked as “Unassigned”', {
            count: upcomingEventCount,
          })}
        </Text>
        <Text as='li'>{t("You'll need to reassign these appointments manually")}</Text>
      </ul>
      <Text as='span'>
        {t('We recommend reassigning these appointments before deactivating {{provider}}', {
          provider,
        })}
      </Text>
    </div>
  );
};
